import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../api/axiosConfig";

// Shown when an administrator has issued a temporary password. The
// backend blocks every other API call until this succeeds.
export default function ChangePassword() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ currentPassword: "", newPassword: "", confirm: "" });
  const [policy, setPolicy] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("token")) navigate("/login", { replace: true });
  }, [navigate]);

  useEffect(() => {
    axiosInstance
      .get("/profile/password-policy")
      .then((r) => setPolicy(r.data?.data || null))
      .catch(() => setPolicy(null));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.newPassword !== form.confirm) {
      setError("The new passwords do not match.");
      return;
    }
    setSaving(true);
    try {
      await axiosInstance.put("/profile/password", {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      // Every existing token is invalidated by the change.
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      navigate("/login", {
        replace: true,
        state: { message: "Password updated. Sign in with your new password." },
      });
    } catch (err) {
      setError(err?.error || err?.message || "Could not update the password.");
    } finally {
      setSaving(false);
    }
  };

  const signOut = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login", { replace: true });
  };

  const rules = policy
    ? [
        `at least ${policy.minLength} characters`,
        policy.requireUpper && "an uppercase letter",
        policy.requireLower && "a lowercase letter",
        policy.requireDigit && "a number",
        policy.requireSymbol && "a symbol",
      ].filter(Boolean)
    : [];

  return (
    <div className="cp-wrap">
      <style>{`
        .cp-wrap { min-height: 100vh; display: flex; align-items: center; justify-content: center; background: #f4f6fb; padding: 16px; }
        .cp-card { width: 100%; max-width: 420px; background: #fff; border-radius: 12px; box-shadow: 0 8px 30px rgba(20,30,70,.08); padding: 28px; }
        .cp-card h1 { font-size: 20px; margin: 0 0 6px; color: #1f2544; }
        .cp-sub { color: #6b7290; font-size: 13px; margin: 0 0 18px; }
        .cp-field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }
        .cp-field label { font-size: 12px; font-weight: 600; color: #3a4166; }
        .cp-field input { border: 1px solid #d6dbe8; border-radius: 8px; padding: 10px 12px; font-size: 14px; }
        .cp-field input:focus { outline: 2px solid #c7cdf5; border-color: #3949ab; }
        .cp-rules { font-size: 12px; color: #6b7290; margin: -4px 0 16px; }
        .cp-error { background: #fdecec; color: #b42318; border-radius: 8px; padding: 10px 12px; font-size: 13px; margin-bottom: 14px; }
        .cp-btn { width: 100%; background: #3949ab; color: #fff; border: 0; border-radius: 8px; padding: 11px; font-size: 14px; font-weight: 600; cursor: pointer; }
        .cp-btn:disabled { opacity: .6; cursor: not-allowed; }
        .cp-link { display: block; margin: 14px auto 0; background: none; border: 0; color: #3949ab; font-size: 13px; cursor: pointer; }
      `}</style>
      <form className="cp-card" onSubmit={submit}>
        <h1>Set a new password</h1>
        <p className="cp-sub">
          Your administrator issued a temporary password. Choose your own before continuing.
        </p>
        {error && <div className="cp-error">{error}</div>}
        <div className="cp-field">
          <label htmlFor="cp-current">Temporary password</label>
          <input id="cp-current" type="password" autoComplete="current-password" required value={form.currentPassword} onChange={set("currentPassword")} />
        </div>
        <div className="cp-field">
          <label htmlFor="cp-new">New password</label>
          <input id="cp-new" type="password" autoComplete="new-password" required value={form.newPassword} onChange={set("newPassword")} />
        </div>
        {rules.length > 0 && <div className="cp-rules">Use {rules.join(", ")}.</div>}
        <div className="cp-field">
          <label htmlFor="cp-confirm">Confirm new password</label>
          <input id="cp-confirm" type="password" autoComplete="new-password" required value={form.confirm} onChange={set("confirm")} />
        </div>
        <button className="cp-btn" type="submit" disabled={saving}>
          {saving ? "Saving…" : "Update password"}
        </button>
        <button className="cp-link" type="button" onClick={signOut}>
          Sign out
        </button>
      </form>
    </div>
  );
}
